import { sideLandmarks, THRESHOLDS } from '@erectus/shared';
import { phoneRow, type PhoneTilt } from '@/features/capture/useTilt.js';
import type { GateResult, Landmark } from '@erectus/shared';
import { standingAlignment } from '@/metrics/standing.js';

/**
 * The checklist for the standing step of a posture check. The seated gate
 * (qualityGate) already decides side view, ear, light and people in frame; the
 * standing step adds the two things only it needs: the feet in frame, and the
 * person actually standing with straight knees.
 */
export type StandingCheckId = 'side_view' | 'full_body' | 'standing' | 'phone_upright' | 'light' | 'just_you';

export interface StandingCheck {
  id: StandingCheckId;
  label: string;
  passed: boolean;
}

/**
 * A straight knee reads near 180°, a seated one near 90°. 160° is a gate, not
 * a posture judgement: it only tells "standing" from "sitting or crouching".
 */
export const STANDING_KNEE_MIN_DEG = 160;

export function standingChecks(
  gate: GateResult | undefined,
  landmarks: readonly Landmark[],
  aspect: number,
  /** Phone tilt; null where the device has no motion sensor (row omitted). */
  phone: PhoneTilt | null = null,
): StandingCheck[] {
  const reasons = gate?.reasons ?? ['noPose'];
  const has = (...r: string[]): boolean => r.some((x) => (reasons as string[]).includes(x));
  const nobody = has('noPose');
  const side = gate?.side ?? 'left';
  const idx = sideLandmarks(side);
  const visible = (i: number, min: number): boolean => (landmarks[i]?.visibility ?? 0) >= min;
  const fullBody =
    !nobody &&
    !has('outOfFrame') &&
    visible(idx.ear, THRESHOLDS.gate.earVisibility) &&
    visible(idx.hip, THRESHOLDS.gate.hipVisibility) &&
    visible(idx.knee, THRESHOLDS.gate.kneeVisibility) &&
    visible(idx.ankle, THRESHOLDS.gate.kneeVisibility) &&
    (landmarks[idx.ankle]?.y ?? 1) < 0.98;
  const alignment = fullBody ? standingAlignment(landmarks, side, aspect) : null;

  return [
    { id: 'side_view', label: 'Side-on to the camera', passed: !nobody && !has('notSideView') },
    { id: 'full_body', label: 'Head to feet in frame', passed: fullBody },
    {
      id: 'standing',
      label: 'Standing, knees straight',
      passed: alignment !== null && alignment.kneeAngle >= STANDING_KNEE_MIN_DEG,
    },
    ...(phone === null ? [] : [phoneRow(phone)]),
    { id: 'light', label: 'Lighting is usable', passed: !has('tooDark', 'tooBright') },
    { id: 'just_you', label: 'Only you are in frame', passed: !nobody && !has('multiPerson') },
  ];
}
